import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { errorMessage, get, patch } from "@/lib/api";
import { EMAIL_RE, parseList, TEAM_ROLE_LABELS, WORKER_ROLE_OPTIONS } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { ErrorState, FieldError, LoadingRows, PageHeader } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useAsync } from "@/lib/queries";

const blank = { email: "", full_name: "", phone: "", areas_covered: "", postcodes: "", active: true, open_jobs_enabled: false, user_types: [] };

export default function PlatformMemberEditPage() {
  const { companyId, memberId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useAuth();
  const returnTo = location.state?.returnTo || "/manager/admin?tab=members";
  const { data: member, loading, error, reload } = useAsync(() => get(`/platform/companies/${companyId}/members/${memberId}`), [companyId, memberId]);
  const [form, setForm] = useState(blank);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const isManager = member?.role === "manager";
  const enabledTypes = member?.enabled_user_types || WORKER_ROLE_OPTIONS.map(([role]) => role);
  const availableTypes = WORKER_ROLE_OPTIONS.filter(([role]) => !(profile?.company?.sitewide_disabled_types || []).includes(role) && (enabledTypes.includes(role) || form.user_types.includes(role)));

  useEffect(() => {
    if (!member) return;
    setForm({
      ...blank,
      email: member.email,
      full_name: member.full_name || "",
      phone: member.phone || "",
      areas_covered: member.areas_covered || "",
      postcodes: (member.postcodes || []).join(", "),
      active: member.membership_active ?? member.active,
      open_jobs_enabled: !!member.open_jobs_enabled,
      user_types: member.user_types || (member.role !== "manager" ? [member.role] : []),
    });
    setErrors({});
  }, [member]);

  if (!profile?.is_platform_owner) return <Navigate to="/manager" replace />;

  const submit = async (event) => {
    event.preventDefault();
    const postcodes = parseList(form.postcodes);
    const nextErrors = {};
    if (!EMAIL_RE.test(form.email.trim())) nextErrors.email = "Enter a valid email address";
    if (form.full_name.trim().length < 2) nextErrors.full_name = "Enter their name";
    if (!isManager && !form.user_types.length) nextErrors.user_types = "Select at least one enabled user type";
    if (postcodes.some((postcode) => !/^[A-Z0-9 ]{1,8}$/.test(postcode))) nextErrors.postcodes = "Use postcode districts like E13, IG11, separated by commas";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setBusy(true);
    try {
      await patch(`/platform/companies/${member.company_id}/members/${member.id}`, {
        email: form.email.trim().toLowerCase(),
        full_name: form.full_name.trim(),
        phone: form.phone.trim() || null,
        areas_covered: form.areas_covered.trim() || null,
        postcodes,
        active: form.active,
        open_jobs_enabled: isManager ? null : form.open_jobs_enabled,
        user_types: isManager ? null : form.user_types,
      });
      toast.success(`${form.full_name.trim()} updated`);
      navigate(returnTo, { replace: true });
    } catch (saveError) {
      setErrors({ form: errorMessage(saveError) });
    } finally {
      setBusy(false);
    }
  };

  const setField = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));

  return (
    <div className="space-y-6" data-testid="admin-member-edit-page">
      <PageHeader eyebrow={`Admin · ${member?.company_name || "Member"}`} title="Edit member"
        description={member ? `${member.full_name} · ${TEAM_ROLE_LABELS[member.role] || member.role}` : "Update this company membership."}
        actions={<Button type="button" variant="outline" onClick={() => navigate(returnTo)} data-testid="admin-member-back-button"><ArrowLeft className="mr-1 h-4 w-4" />Back to members</Button>} />
      {error && <ErrorState message={error} onRetry={reload} />}
      {loading && !member && <LoadingRows rows={4} />}
      {member && <form onSubmit={submit} noValidate className="max-w-3xl space-y-5" data-testid="admin-member-form">
        <section className="space-y-4 rounded-lg border border-slate-200 bg-white p-5">
          <h2 className="font-display text-base font-bold text-slate-900">Account details</h2>
          <div>
            <Label htmlFor="admin-member-email">Email</Label>
            <Input id="admin-member-email" type="email" value={form.email} onChange={setField("email")} className="mt-1.5" data-testid="admin-member-email-input" />
            <p className="mt-1 text-xs text-slate-500">This is their sign-in email across all companies.</p>
            <FieldError message={errors.email} testId="admin-member-email-error" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="admin-member-name">Full name</Label>
              <Input id="admin-member-name" value={form.full_name} onChange={setField("full_name")} className="mt-1.5" data-testid="admin-member-name-input" />
              <FieldError message={errors.full_name} testId="admin-member-name-error" />
            </div>
            <div>
              <Label htmlFor="admin-member-phone">Phone</Label>
              <Input id="admin-member-phone" type="tel" value={form.phone} onChange={setField("phone")} className="mt-1.5" data-testid="admin-member-phone-input" />
            </div>
          </div>
          <div>
            <Label htmlFor="admin-member-areas">Areas covered</Label>
            <Textarea id="admin-member-areas" rows={2} value={form.areas_covered} onChange={setField("areas_covered")} className="mt-1.5" data-testid="admin-member-areas-input" />
          </div>
          <div>
            <Label htmlFor="admin-member-postcodes">Postcodes covered</Label>
            <Input id="admin-member-postcodes" value={form.postcodes} onChange={setField("postcodes")} className="mt-1.5 font-data uppercase" placeholder="E6, E13, IG11" data-testid="admin-member-postcodes-input" />
            <FieldError message={errors.postcodes} testId="admin-member-postcodes-error" />
          </div>
        </section>
        {!isManager && <section className="space-y-4 rounded-lg border border-slate-200 bg-white p-5">
          <h2 className="font-display text-base font-bold text-slate-900">Company membership</h2>
          <fieldset className="space-y-2">
            <legend className="text-sm font-semibold text-slate-900">User types</legend>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {availableTypes.map(([role, label]) => (
                <label key={role} className="flex min-h-[44px] cursor-pointer items-center gap-2 rounded-md border border-slate-200 p-3 text-sm">
                  <Checkbox checked={form.user_types.includes(role)} onCheckedChange={(checked) => setForm((current) => ({
                    ...current,
                    user_types: checked ? [...current.user_types, role] : current.user_types.filter((type) => type !== role),
                  }))} data-testid={`admin-member-type-${role}`} />
                  {label}
                </label>
              ))}
            </div>
            <FieldError message={errors.user_types} testId="admin-member-types-error" />
          </fieldset>
          <label htmlFor="admin-member-open-jobs" className="flex cursor-pointer items-start gap-3 rounded-md border border-slate-200 p-3">
            <Checkbox id="admin-member-open-jobs" checked={form.open_jobs_enabled} onCheckedChange={(checked) => setForm((current) => ({ ...current, open_jobs_enabled: checked === true }))} className="mt-0.5" data-testid="admin-member-open-jobs-checkbox" />
            <span className="text-sm"><span className="block font-medium text-slate-900">Show Available Jobs</span><span className="block text-xs text-slate-500">Allow this member to see and accept open runs.</span></span>
          </label>
        </section>}
        <div className="flex items-center justify-between rounded-md border border-slate-200 bg-white p-4">
          <Label htmlFor="admin-member-active">Active in this company</Label>
          <Switch id="admin-member-active" checked={form.active} onCheckedChange={(active) => setForm((current) => ({ ...current, active }))} data-testid="admin-member-active-switch" />
        </div>
        {errors.form && <p role="alert" className="rounded-md bg-rose-50 p-3 text-sm text-rose-700" data-testid="admin-member-form-error">{errors.form}</p>}
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={() => navigate(returnTo)} disabled={busy} data-testid="admin-member-cancel-button">Cancel</Button>
          <Button type="submit" disabled={busy} className="bg-blue-700 hover:bg-blue-800" data-testid="admin-member-save-button">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save member
          </Button>
        </div>
      </form>}
    </div>
  );
}
